import { useContext } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkDashRule } from '@arthelper/shared/chat-markdown';
import { markdownComponents, publicMarkdownComponents } from './tool-renderers/common';
import ToolCallWrapper from './ToolCall';
import { useChatUI } from '~/context/chatUI';
import { getToolCallFromPart } from './toolPartAdapter';
import { ArtyImageBox } from '~/components/arty/card';
import { ArtyRenderContext } from './artyRenderContext';

// Canonical PascalCase names. The model is instructed to emit these (see
// `api/src/ai/agents/simple.ts`), but it occasionally drifts to the HTML
// custom-element convention `<image-box>`. Accept both forms here so the
// renderer is tolerant to that drift instead of leaking raw tags into the UI.
const COMPONENT_NAMES = ['ImageBox', 'ShowUpload', 'DailySMSTipsSignup'] as const;
type ComponentName = (typeof COMPONENT_NAMES)[number];

function pascalToKebab(name: string): string {
  // Two passes so consecutive-uppercase runs (e.g. `SMSTips`) split correctly
  // into `SMS-Tips` rather than collapsing into `smstips`.
  return name
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();
}

const COMPONENT_TAG_ALTERNATION = COMPONENT_NAMES.flatMap((n) => [n, pascalToKebab(n)]).join('|');

// Matches a complete known component tag, e.g. `<ImageBox ... />` or `<image-box ... />`.
// No `g` flag — used with `.test()` and we need stateless matching.
// Uses `(?=[\s/>])` instead of `\b` so that kebab names like `image-box` don't
// spuriously match superset tags like `<image-box-loading>` (where `\b` fires
// between the word-char `x` and the hyphen `-`).
const KNOWN_COMPONENT_TAG_RE = new RegExp(`<(?:${COMPONENT_TAG_ALTERNATION})(?=[\\s/>])[^>]*\\/?>`);

// Splits content while preserving known component tags as their own parts.
const KNOWN_COMPONENT_SPLIT_RE = new RegExp(`(<(?:${COMPONENT_TAG_ALTERNATION})(?=[\\s/>])[^>]*\\/?>)`, 'g');

// Matches an incomplete component tag during streaming (suppresses raw-text flash).
// Covers known names, any PascalCase, and kebab-case names with at least one hyphen
// (e.g. `<image-bo`). Requiring a hyphen is the HTML spec rule that distinguishes
// custom elements from built-in tags, preventing `<div`, `<span`, etc. from matching.
const INCOMPLETE_COMPONENT_RE = new RegExp(`<(?:${COMPONENT_TAG_ALTERNATION}|[A-Z]\\w*|[a-z]\\w*-[\\w-]*)[^>]*$`);

function hasIncompleteComponent(content: string): boolean {
  return INCOMPLETE_COMPONENT_RE.test(content);
}

function removeIncompleteComponent(content: string): string {
  return content.replace(INCOMPLETE_COMPONENT_RE, '');
}

function hasComponents(content: string): boolean {
  return KNOWN_COMPONENT_TAG_RE.test(content);
}

function canonicalizeTagName(part: string): ComponentName | null {
  const match = part.match(/^<([A-Za-z][\w-]*)/);
  if (!match) return null;
  const tag = match[1];
  return COMPONENT_NAMES.find((name) => name === tag || pascalToKebab(name) === tag) ?? null;
}

function renderMarkdown(content: string, mode?: string) {
  const selectedComponents = mode === 'public' ? publicMarkdownComponents : markdownComponents;

  return (
    <Markdown components={selectedComponents} remarkPlugins={[remarkGfm, remarkDashRule]}>
      {content}
    </Markdown>
  );
}

const SHOW_UPLOAD_TAG_RE = /<(?:ShowUpload|show-upload)(?=[\s/>])[^>]*\/?>/g;

function stripShowUploadTags(content: string): string {
  return content.replace(SHOW_UPLOAD_TAG_RE, '');
}

function renderMixed(
  content: string,
  onUploadComplete?: any,
  mode?: string,
  suppressShowUpload?: boolean,
  uploadAckRetryKey?: string
) {
  return (
    <MixedMDXContent
      content={content}
      onUploadComplete={onUploadComplete}
      mode={mode}
      suppressShowUpload={suppressShowUpload}
      uploadAckRetryKey={uploadAckRetryKey}
    />
  );
}

function processContent(
  content: string,
  isStreaming: boolean,
  onUploadComplete?: any,
  mode?: string,
  suppressShowUpload?: boolean,
  uploadAckRetryKey?: string
) {
  const normalizedContent = suppressShowUpload ? stripShowUploadTags(content) : content;

  if (isStreaming && hasIncompleteComponent(normalizedContent)) {
    const cleanContent = removeIncompleteComponent(normalizedContent);
    return renderMarkdown(cleanContent, mode);
  }

  if (!hasComponents(normalizedContent)) {
    return renderMarkdown(normalizedContent, mode);
  }

  return renderMixed(normalizedContent, onUploadComplete, mode, suppressShowUpload, uploadAckRetryKey);
}

function parseQuotedAttribute(text: string, attr: string): string | undefined {
  const doubleQuoted = text.match(new RegExp(`${attr}="([^"]+)"`, 'i'));
  if (doubleQuoted) {
    return doubleQuoted[1];
  }

  const singleQuoted = text.match(new RegExp(`${attr}='([^']+)'`, 'i'));
  return singleQuoted?.[1];
}

function parseAttributes(text: string, attrs: string[]) {
  const result: any = {};
  attrs.forEach((attr) => {
    const value = parseQuotedAttribute(text, attr);
    if (value) result[attr] = value;
  });
  return result;
}

const COMPONENT_HANDLERS = {
  ImageBox: (text: string, _onUploadComplete?: any, mode?: string) => {
    const { src, alt, imageId } = parseAttributes(text, ['src', 'alt', 'imageId']);

    // Parse instagramCaption separately since it can contain quotes and multiline text
    const captionMatch = text.match(/instagramCaption=(?:"(.*?)"|'(.*?)')\s*\/?>/s);
    const instagramCaption = captionMatch ? (captionMatch[1] ?? captionMatch[2]) : undefined;

    if (!src && !imageId) {
      return null;
    }

    return <ImageBoxPart src={src} alt={alt} imageId={imageId} instagramCaption={instagramCaption} mode={mode} />;
  },
  ShowUpload: (_text: string, onUploadComplete?: any, _mode?: string, uploadAckRetryKey?: string) => {
    return <markdownComponents.ShowUpload onUploadComplete={onUploadComplete} uploadAckRetryKey={uploadAckRetryKey} />;
  },
  DailySMSTipsSignup: (text: string) => {
    const { title, description } = parseAttributes(text, ['title', 'description']);
    return <markdownComponents.DailySMSTipsSignup title={title} description={description} />;
  },
};

function ImageBoxPart({ src, alt, imageId, instagramCaption, mode }: any) {
  const { card } = useContext(ArtyRenderContext);

  // The agent puts a caption in the prop on nearly every box, so the card cannot key on it;
  // in the flyout the card is the box, as in town-square, and the caption stays out. A box with
  // only a src goes to the card too: it recovers the id from the URL, or draws the bare image.
  if (card) {
    return <ArtyImageBox imageId={imageId} src={src} alt={alt} />;
  }

  const selectedComponents = mode === 'public' ? publicMarkdownComponents : markdownComponents;
  return (
    <selectedComponents.ImageBox src={src ?? ''} alt={alt} imageId={imageId} instagramCaption={instagramCaption} />
  );
}

function processComponent(
  part: string,
  onUploadComplete?: any,
  mode?: string,
  suppressShowUpload?: boolean,
  uploadAckRetryKey?: string
) {
  const canonical = canonicalizeTagName(part);
  if (canonical === 'ShowUpload' && suppressShowUpload) {
    return null;
  }
  if (canonical && canonical in COMPONENT_HANDLERS) {
    return COMPONENT_HANDLERS[canonical](part, onUploadComplete, mode, uploadAckRetryKey);
  }
  return renderMarkdown(part, mode);
}

function MixedMDXContent({
  content,
  onUploadComplete,
  mode,
  suppressShowUpload,
  uploadAckRetryKey,
}: {
  content: string;
  onUploadComplete?: any;
  mode?: string;
  suppressShowUpload?: boolean;
  uploadAckRetryKey?: string;
}) {
  const parts = content.split(KNOWN_COMPONENT_SPLIT_RE);

  return (
    <div>
      {parts.map((part, index) => {
        if (!part.trim()) return null;

        return (
          <div key={index}>{processComponent(part, onUploadComplete, mode, suppressShowUpload, uploadAckRetryKey)}</div>
        );
      })}
    </div>
  );
}

function MessagePart({
  part,
  messageId,
  idx,
  isStreaming,
  onUploadComplete,
  suppressShowUpload,
  uploadAckRetryKey,
}: {
  part: any;
  messageId: string;
  idx: number;
  isStreaming?: boolean;
  onUploadComplete?: any;
  suppressShowUpload?: boolean;
  uploadAckRetryKey?: string;
}) {
  const { mode } = useChatUI();

  if (part.type === 'text') {
    return (
      <div className="text-gray-800">
        {processContent(part.text, isStreaming || false, onUploadComplete, mode, suppressShowUpload, uploadAckRetryKey)}
      </div>
    );
  }

  // Reasoning parts are never rendered. They are the model's chain of thought, not its answer:
  // a failed turn would otherwise leave the thinking as the only assistant content on screen.
  // `AssistantMessage` filters them out before this point; this guard covers any other caller.
  if (part.type === 'reasoning') {
    return null;
  }

  const toolCall = getToolCallFromPart(part);
  if (toolCall) {
    return (
      <div key={`tool-${messageId}-${idx}`} className="mt-4 mb-4">
        <ToolCallWrapper toolCall={toolCall} />
      </div>
    );
  }

  return null;
}

export default MessagePart;
